<template>
    <v-card class="barcodes" outlined>
        <v-card-title class="page-heading">
            {{ $t('allegra.barcodes.heading') }}
        </v-card-title>

        <v-card-text class="pa-5">
            <div v-for="(pattern, index) in form.patterns" :key="index">
                <v-row>
                    <v-col cols="12" sm="2">
                        <v-text-field
                            v-model="pattern.prefix"
                            class="barcode-field"
                            :error-messages="form.$errors[`patterns.${index}.prefix`]"
                            :hint="$t('allegra.barcodes.prefix')"
                            persistent-hint
                            outlined
                            validate-on-blur
                            @blur="form.validate(`patterns.${index}.prefix`)"
                            @input="delete form.$errors[`patterns.${index}.prefix`]"
                        />
                    </v-col>

                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="pattern.product_code_digits"
                            class="barcode-field"
                            :error-messages="form.$errors[`patterns.${index}.product_code_digits`]"
                            :hint="$t('allegra.barcodes.product_code_digits')"
                            persistent-hint
                            readonly
                            outlined
                            validate-on-blur
                            @blur="form.validate(`patterns.${index}.product_code_digits`)"
                            @input="delete form.$errors[`patterns.${index}.product_code_digits`]"
                        >
                            <template slot="prepend-inner">
                                <v-btn
                                    class="mt-n2"
                                    icon
                                    :disabled="pattern.product_code_digits <= 4"
                                    @click="pattern.product_code_digits--"
                                >
                                    <v-icon>fal fa-minus</v-icon>
                                </v-btn>
                            </template>

                            <template slot="append">
                                <v-btn
                                    class="mt-n2"
                                    icon
                                    @click="pattern.product_code_digits++"
                                >
                                    <v-icon>fal fa-plus</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="pattern.product_count_digits"
                            class="barcode-field"
                            :error-messages="form.$errors[`patterns.${index}.product_count_digits`]"
                            :hint="$t('allegra.barcodes.product_count_digits')"
                            persistent-hint
                            readonly
                            outlined
                            validate-on-blur
                            @blur="form.validate(`patterns.${index}.product_count_digits`)"
                            @input="delete form.$errors[`patterns.${index}.product_count_digits`]"
                        >
                            <template slot="prepend-inner">
                                <v-btn
                                    class="mt-n2"
                                    icon
                                    :disabled="pattern.product_count_digits <= 4"
                                    @click="pattern.product_count_digits--"
                                >
                                    <v-icon>fal fa-minus</v-icon>
                                </v-btn>
                            </template>

                            <template slot="append">
                                <v-btn
                                    class="mt-n2"
                                    icon
                                    @click="pattern.product_count_digits++"
                                >
                                    <v-icon>fal fa-plus</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col cols="12" sm="3">
                        <v-text-field
                            v-model="pattern.product_count_decimals"
                            class="barcode-field"
                            :error-messages="form.$errors[`patterns.${index}.product_count_decimals`]"
                            :hint="$t('allegra.barcodes.product_count_decimals')"
                            persistent-hint
                            readonly
                            outlined
                            validate-on-blur
                            @blur="form.validate(`patterns.${index}.product_count_decimals`)"
                            @input="delete form.$errors[`patterns.${index}.product_count_decimals`]"
                        >
                            <template slot="prepend-inner">
                                <v-btn
                                    class="mt-n2"
                                    dense
                                    icon
                                    :disabled="pattern.product_count_decimals <= 0"
                                    @click="pattern.product_count_decimals--"
                                >
                                    <v-icon>fal fa-minus</v-icon>
                                </v-btn>
                            </template>

                            <template slot="append">
                                <v-btn
                                    class="mt-n2"
                                    icon
                                    @click="pattern.product_count_decimals++"
                                >
                                    <v-icon>fal fa-plus</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                    </v-col>

                    <v-col class="mt-2 d-flex justify-center" cols="12" sm="1">
                        <v-btn
                            v-if="(index === 0 && form.patterns.length === 1) || index !== 0 && index === form.patterns.length - 1"
                            color="primary"
                            icon
                            @click="addBarcodePattern"
                        >
                            <v-icon>fal fa-plus-circle</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="index === form.patterns.length - 1"
                            color="error"
                            :disabled="index === 0"
                            icon
                            @click="removeBarcodePattern"
                        >
                            <v-icon>fal fa-trash-alt</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>

                <v-divider v-if="index !== form.patterns.length - 1" class="mt-3 mb-6" />
            </div>

            <v-btn
                class="mt-7"
                color="primary"
                depressed
                block
                :loading="form.$busy"
                :disabled="form.$busy"
                @click="updateBarcodePattern"
            >
                {{ $t('general.save') }}
            </v-btn>
        </v-card-text>
    </v-card>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import * as mutationTypes from '@/store/mutationTypes';
import * as rules from 'vuelidate/lib/validators'

export default {
    created() {
        loadData('/admin/load/barcodes').then(data => this.onDataLoaded(data));
    },

    data() {
        return {
            form: this.createForm(),
        };
    },

    validations: {
        form: {
            patterns: {
                $each: {
                    prefix: {
                        required: rules.required,
                        unique: rules.helpers.withParams(
                            { type: 'unique' },
                            function(value) {
                                if(!value) return true

                                return this.form.patterns.map(pattern => pattern.prefix)
                                    .filter(val => val == value)
                                    .length <= 1
                            }
                        )
                    },
                    product_code_digits: { required: rules.required },
                    product_count_digits: { required: rules.required },
                    product_count_decimals: {
                        required: rules.required,
                        maxDecimals: rules.helpers.withParams(
                            { type: 'maxDecimals' },
                            function(value, pattern) {
                                if(!value) return true

                                return parseInt(value) < parseInt(pattern.product_count_digits)
                            }
                        )
                    }
                }
            }
        }
    },

    methods: {
        onDataLoaded(data) {
            this.form = this.createForm(data.barcode_pattern.split('|').map(pattern => pattern.split(',')))
        },

        createForm(patterns = []) {
            const formPatterns = []
            patterns.forEach(pattern => {
                formPatterns.push({
                    prefix: pattern[0] || null,
                    product_code_digits: pattern[1] || null,
                    product_count_digits: pattern[2] || null,
                    product_count_decimals: pattern[3] || 3,
                })
            })

            return new Form(this, 'form', {
                patterns: formPatterns
            })
        },

        addBarcodePattern() {
            this.form.patterns.push({
                prefix: null,
                product_code_digits: 5,
                product_count_digits: 6,
                product_count_decimals: 3,
            })
        },

        removeBarcodePattern() {
            if(this.form.patterns.length === 1) return
            this.form.patterns = this.form.patterns.slice(0, this.form.patterns.length - 1)
        },

        updateBarcodePattern() {
            if (!this.form.validate()) return

            ApiService.patch('/admin/barcodes', this.form).then(response => {
                this.$store.commit(mutationTypes.SET_TENANT, { tenant: response.data.tenant });

                this.$snackbar(this.$t('allegra.barcodes.updated'), 'success');
            })
        }
    }
};
</script>

<style lang="scss" scoped>
.barcodes {
    max-width: 900px;

    .page-heading {
        padding: 12px 16px;
        color: white;
        background: var(--v-primary-base);
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .barcode-field {
        .v-messages__message {
            text-align: center;
        }

        input {
            text-align: center;
        }
    }
}
</style>
